import moment from "moment";
import React, { useState, useContext } from "react";
import { Backdrop, Tooltip } from "@mui/material";

import ADataGrid from "views/pages/manage/common/data-grid/ADataGrid";
import ALoader from "views/pages/manage/common/a-loader";

import AButton from "views/pages/manage/common/form-fields-mui/AButton";
import { AGridColDef } from "views/pages/manage/common/data-grid/ADataGridColDef";
import {
  successToast,
  waitingToast,
} from "views/pages/manage/common/utils/methods/toasterFunctions/function";
import { isActiveBusinessArea } from "views/pages/manage/unAssigned/collectPayment/utils.jsx";

import { formattedPrice } from "../../../../unAssigned/collectPayment/utils";
import { submitstatusHandler } from "../../../../unAssigned/studentFinanceApproval/epmrCancellation/utils";
import ConfirmApprovalDialog from "../../../../unAssigned/studentFinanceApproval/epmrCancellation/ConfirmApprovalDialogDetail";
import ConfirmRejectionDialog from "../../../../unAssigned/studentFinanceApproval/epmrCancellation/ConfirmRejectionDialog";

import {
  BUPopover,
  CourseTermPopover,
  ReceiptStatus,
  RemarksPopover,
} from "./ColumnPopovers";
import NoReceiptsFound from "./NoReceiptsFound";

import { RolePermissions, PermissionContext } from "appContext";
import { pages } from "views/pages/manage/common/constant";
import CustomChip from "../../../../common/customChip/CustomChip";
import { epmrStatusChipInfo } from "./constants";

export const getEpmrStatus = row => {
  const { isApproved, status } = row;
  if (isApproved) return "Cancelled";
  if (status?.toLowerCase() === 'confirmed') return 'Confirmed';
  return 'Not Confirmed';
}

const EpmrReceiptsListing = ({
  rows,
  isLoading,
  onPageChange,
  rowCount,
  currentPage,
  updateReceiptsList,
  businessAreaOptions,
}) => {
  const userPermissions =
    RolePermissions(
      useContext(PermissionContext),
      pages["epmrListing"]["id"]
    ) || [];

  const [isCancel, setIsCancel] = useState(false);
  const [isConfirm, setIsConfirm] = useState(false);
  const [rowDetails, setRowDetails] = useState(null);
  const [isUpdatingStatus, setiIsUpdatingStatus] = useState(false);

  const formattedDate = (timestamp) => moment(timestamp).format("DD/MM/YYYY");

  const formattedDateTime = (timestamp) =>
    moment(timestamp).format("DD/MM/YYYY, hh:mm A");

  const lastUpdatedByOn = (updatedOn = "", updatedBy = "") =>
    `${formattedDateTime(updatedOn)} by ${updatedBy ?? "-"}`;

  const columns = [
    new AGridColDef("epmrNumber", "EPMR No.")
      .setMinWidth(125)
      // .sort(true)
      .renderCellComponent((params) =>
        params?.value ? (
          <Tooltip title={params?.value}>
            <div className={`ellipsis-text-container`}>{params.value}</div>
          </Tooltip>
        ) : (
          <div>-</div>
        )
      ),

    new AGridColDef("businessAreaValue", "Branch Code", false)
      .setMinWidth(80)
      .renderCellComponent((params) => (
        <div>
          <span>{params.value}</span>&nbsp;
          <span>
            <BUPopover
              businessArea={params.row.businessAreaId}
              businessAreaOptions={businessAreaOptions}
            />
          </span>
        </div>
      )),

    new AGridColDef("psid", "PSID")
      .setMinWidth(115)
      .renderCellComponent((params) =>
        params?.value ? (
          <Tooltip title={params?.value}>
            <div className={`ellipsis-text-container`}>{params.value}</div>
          </Tooltip>
        ) : (
          <div>-</div>
        )
      ),
    new AGridColDef("studentName", "Student name")
      .setMinWidth(150)
      .renderCellComponent((params) =>
        params?.value ? (
          <Tooltip title={params?.value}>
            <div className={`ellipsis-text-container`}>{params.value}</div>
          </Tooltip>
        ) : (
          <div>-</div>
        )
      ),

    new AGridColDef("courseId", "Course ID")
      .setMinWidth(90)
      .renderCellComponent((params) => (
        <div>
          <span>{params.value}</span>&nbsp;
          <span>
            <CourseTermPopover
              courseId={params?.value}
              term={params?.row.termValue}
            />
          </span>
        </div>
      )),

    new AGridColDef("totatAmountReceived", "Total Amount Received")
      .setMinWidth(130)
      .renderCellComponent((params) => (
        <span className="ml-auto">
          {params?.value ? formattedPrice(params.value) : "-"}
        </span>
      )),

    new AGridColDef("paymentMode", "Payment mode", false)
      .setMinWidth(100)
      .renderCellComponent((params) =>
        params?.value ? (
          <Tooltip title={params?.value}>
            <div className={`mx-auto ellipsis-text-container`}>
              {params?.value}
            </div>
          </Tooltip>
        ) : (
          <div>-</div>
        )
      ),

    new AGridColDef("remark", "Remarks", false)
      .setMinWidth(100)
      .renderCellComponent((params) => {
        const status = params.row?.status;
        return ["PENDING", "L1_REJECT", "L2_REJECT"].includes(status) ? (
          <RemarksPopover
            epmrNumber={params?.row?.epmrNumber}
            status={status}
          />
        ) : (
          <div>-</div>
        );
      }),

    new AGridColDef("epmrStatus", "EPMR Status", false)
      .setMinWidth(120)
      .renderCellComponent((params) => 
        <CustomChip style={{fontSize : '12px'}} {...epmrStatusChipInfo[getEpmrStatus(params?.row)]}/>
      ),

    new AGridColDef("receiptDate", "EPMR Date")
      .setMinWidth(140)
      .renderCellComponent((params) =>
        params?.value ? (
          <Tooltip title={formattedDate(params?.value)}>
            <div className="ellipsis-text-container">
              {formattedDate(params?.value)}
            </div>
          </Tooltip>
        ) : (
          <div className="m-auto">-</div>
        )
      ),

    new AGridColDef("updatedOn", "Last Updated On/By")
      .setMinWidth(170)
      .renderCellComponent((params) =>
        params?.value ? (
          <Tooltip title={lastUpdatedByOn(params?.value, params.row.updatedBy)}>
            <div className="ellipsis-text-container">
              {lastUpdatedByOn(params?.value, params.row.updatedBy)}
            </div>
          </Tooltip>
        ) : (
          <div className="m-auto">-</div>
        )
      ),

    new AGridColDef("status", "Status", false)
      .setMinWidth(140)
      .renderCellComponent((params) =>
        params?.value ? (
          <Tooltip title={params?.value}>
            <ReceiptStatus
              status={params?.value.toLowerCase()}
              isApproved={params?.row.isApproved}
            />
          </Tooltip>
        ) : (
          <div>-</div>
        )
      ),

    // ! column available with only C,U permissions
    ...(userPermissions?.includes("C") || userPermissions?.includes("U")
      ? [
          new AGridColDef("actions", "Action", false)
            .setMinWidth(220)
            .pinColumnLast(true)
            .setFlex(0.8)
            .editable(false)
            .renderCellComponent((params) => {
              return isActiveBusinessArea({
                buLabel: params.row.businessAreaValue,
              }) ? (
                <div>
                  {["NEW", "L1_REJECT", "L2_REJECT"].includes(
                    params.row?.status
                  ) && (
                    <>
                      {/* <AButton
                        variant="outline_danger"
                        size="xs"
                        className="no-ml-btn"
                        onClick={() => {
                          setIsCancel(true);
                          setRowDetails(params.row);
                        }}
                      >
                        Cancel
                      </AButton> */}

                      <AButton
                        variant="primary_filled"
                        size="xs"
                        onClick={() => {
                          setIsConfirm(true);
                          setRowDetails(params.row);
                        }}
                      >
                        Confirm
                      </AButton>
                    </>
                  )}

                  {params?.row?.status?.toLowerCase() === "confirmed" && (
                      <>
                        <AButton
                          variant="outline_danger"
                          size="xs"
                          className="no-ml-btn"
                          onClick={() => {
                            setIsCancel(true);
                            setRowDetails(params.row);
                          }}
                        >
                          Cancel
                        </AButton>

                        <AButton
                          variant="primary_filled"
                          size="xs"
                          // className="ml-0"
                          onClick={() => {
                            navigateToCollectPayment({
                              applicationId: params?.row.applicationId,
                            });
                          }}
                        >
                          Collect Now
                        </AButton>

                      </>
                  )}
                </div>
              ) : (
                <></>
              );
            }),
        ]
      : []),
  ];

  const statusHandler = async (rowDetails, approverRemark, approvalStatus) => {
    const payload = {
      approvalStatus,
      epmrNumber: rowDetails.epmrNumber,
      approverRemark,
    };
    setiIsUpdatingStatus(true);

    const response = await submitstatusHandler(payload);

    setiIsUpdatingStatus(false);
    if (response?.code === 200) {
      isConfirm
        ? successToast(
            `EPMR of Rs ${response.data.totatAmountReceived} has been confirmed successfully.`
          )
        : waitingToast(
            "EPMR cancellation request has been sent for the approval."
          );
      updateReceiptsList(); // fecth fresh API results
      // close all dialogs
      setIsCancel(false);
      setIsConfirm(false);
    }
  };

  const navigateToCollectPayment = ({ applicationId }) => {
    const url = `${window.location.origin}/#/admin/student/collectPayment/${applicationId}?from=EPMR`;

    window.open(url, "_blank").focus();
  };

  return (
    <>
      <div style={{ height: "100%" }}>
        <ADataGrid
          rows={rows}
          columns={columns}
          rowId={(row) => row.id}
          loading={isLoading}
          emptyRowPlaceholder={() => <NoReceiptsFound />}
          rowCount={rowCount}
          nextButtonHandler={(page) => {
            onPageChange(page);
          }}
          currentPage={currentPage}
          pinLastColumn
          height="100%"
        />

        {isUpdatingStatus && (
          <Backdrop
            sx={(theme) => ({
              color: "#fff",
              zIndex: theme.zIndex.drawer + 1300,
            })}
            open={isUpdatingStatus}
          >
            <ALoader />
          </Backdrop>
        )}
      </div>
      <ConfirmRejectionDialog
        isOpen={isCancel}
        setOpen={setIsCancel}
        rejectionDetail={rowDetails}
        statusHandler={statusHandler}
        status="PENDING"
        title="Request for cancellation?"
        subheading="Your request or cancellation will be send for approval and once approved it will be cancelled"
      />
      <ConfirmApprovalDialog
        isOpen={isConfirm}
        setOpen={setIsConfirm}
        approvalDetail={rowDetails}
        statusHandler={statusHandler}
        status="CONFIRMED"
        title="Confirm EPMR Request"
      />
    </>
  );
};

export default EpmrReceiptsListing;
